/**
 * Unread conversations for the Messages tab's red badge (Traci, 28 Sep 2026). Same view and audience rules as the
 * Messages page; refreshed every 30 seconds. This person's own unread counts once message_reads exists (Traci's doc,
 * Oct 2026), and for Billing / Editing the conversations their department started.
 */
import { useEffect, useState } from 'react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { unreadForRole, withPersonalUnread, ROUTED_ONLY } from '@/lib/messages/conversations';
import { stagingFetch } from './api';

export function useMessagesUnread(role: string): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [mine, routes] = await Promise.all([
        stagingFetch(`/api/admin/staging/messages/mine?role=${encodeURIComponent(role)}`).catch(() => null),
        ROUTED_ONLY.has(role) ? stagingFetch('/api/admin/staging/messages/routes').catch(() => null) : Promise.resolve(null)
      ]);
      // My own counts need every conversation's audience; the shared ones need only the unread conversations
      const personal = mine?.installed ? mine.unread : null;
      let q = supabaseAdmin.from('admin_conversations').select('user_id, user_role, unread_count');
      if (!personal) q = q.gt('unread_count', 0);
      const { data, error } = await q;
      if (cancelled || error) return;
      let tags: Record<string, string[]> | null = null;
      if (routes?.installed) {
        tags = {};
        for (const r of routes.routes || []) (tags[r.conversation_user_id] ||= []).push(r.department);
      }
      setCount(unreadForRole(withPersonalUnread(data || [], personal), role, tags, mine?.started ? new Set(mine.started as string[]) : null));
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [role]);
  return count;
}
